import React from 'react';
import PropTypes from 'prop-types';

// Component imports
import Ad from '../../components/Ad/Ad';
import { AdSizes } from '../../components/Ad/AdSizes';
import { FeaturedShowPropTypes } from '../../components/FeaturedShows/FeaturedShow';
import FeaturedShowsBlock from '../../components/FeaturedShows/FeaturedShows';
import { FEATURED_SHOWS_QUERY } from '../../components/FeaturedShows/FeaturedShowsQuery';
import AdobeAnalyticsDataLayer from '../../components/AdobeAnalytics/AdobeAnalyticsDataLayer';
import SeoYoast from '../../components/Seo/SeoYoast';
import ShowsNavigation from '../../components/ShowsNavigation/ShowsNavigation';

// HOC imports
import { withDelayedScrollRestoration } from '../../hoc/withDelayedScrollRestoration';
import { withUSGeoBlocking } from '../../hoc/withUSGeoBlocking';

// Util imports
import { initializeApollo } from '../../utils/apolloClient';
import { isUsTraffic, US_CONTENT, REDIRECT_URL } from '../../utils/geoBlock';
import { copySort } from '../../utils/utility';

// Page constants
const PAGE_TITLE = 'Shows';
const PAGE_TITLE_KEBAB = 'shows';

/**
 * @summary Remove shows from a list of shows with a given distribution.
 * @param {Array<import('../../components/FeaturedShows/FeaturedShowsTypes').Show>} shows
 * @param {import('../../components/FeaturedShows/FeaturedShowsTypes').ContentDistribution} distribution
 */
function removeShowsWithDistribution(shows, distribution) {
	return shows.filter((show) => {
		const dists = show?.distributions ?? [];

		return dists.length === 0
			|| dists.some((dist) => dist !== distribution);
	});
}

/**
 * @summary The Featured Shows Page
 *
 * @param {import('../../components/FeaturedShows/FeaturedShowsTypes').FeaturedShowsPageProps} props
 *
 * @see `../../components/FeaturedShows/FeaturedShowTypes.d.ts` for more info on the props.
 * However, each prop has a JSDoc comment originating from this file.
 *
 * @returns {JSX.Element}
 */
function FeaturedShows({
	id = '',
	shows = [],
	seo = {},
}) {
	return (
		<>
			<SeoYoast seo={seo} />
			<Ad
				sizes={{
					'xs sm': AdSizes.MOBILE_LEADERBOARD,
					'md lg': AdSizes.LEADERBOARD,
					'xl xxl': AdSizes.LEADERBOARD_COMBO,
				}}
				htmlId={`${PAGE_TITLE_KEBAB}-leaderboard-ad`}
				wrapperClass={`${PAGE_TITLE_KEBAB}-leaderboard-ad leaderboard-ad`}
			/>
			<AdobeAnalyticsDataLayer
				viewName="shows"
				viewType="archive"
			/>
			<div className={PAGE_TITLE_KEBAB}>
				<div className={`${PAGE_TITLE_KEBAB}-header`}>
					<h1>{PAGE_TITLE}</h1>
					<ShowsNavigation />
				</div>
				<div className={`${PAGE_TITLE_KEBAB}-block`}>
					<FeaturedShowsBlock shows={shows} pageId={PAGE_TITLE_KEBAB} withAds />
				</div>
			</div>
		</>
	);
}

export default withDelayedScrollRestoration(
	withUSGeoBlocking(
		FeaturedShows,
	),
);

/** @type {import('next').GetServerSideProps<import('../../components/FeaturedShows/FeaturedShowsTypes').FeaturedShowsPageProps>} */
export async function getServerSideProps(context) {
	try {
		const apolloClient = initializeApollo();

		const res = await apolloClient.query({
			query: FEATURED_SHOWS_QUERY,
			variables: {
				id: PAGE_TITLE_KEBAB,
				idType: 'URI',
			},
			errorPolicy: 'all',
		});

		// If there is no data for this page,
		// treat it like a 404.
		if (!res?.data?.page) {
			return { notFound: true };
		}

		/** @type {string} */
		const id = res?.data?.page?.databaseId ? res.data.page.databaseId.toString() : '';

		/** @type {Array<import('../../components/FeaturedShows/FeaturedShowsTypes').Show>} */
		const shows = res?.data?.page?.featuredShowsSettings?.showTagsSelector ?? [];

		/** @type {import('../../components/Seo/SeoYoastTypes').Props['seo']} */
		const seo = res?.data?.page?.seo ?? {};

		// Pinned shows are the first 3 the editors selected.
		const pinnedShows = shows.slice(0, 3);

		// Sort the rest of the shows.
		const restShows = shows.slice(3, shows.length);

		// Sort the shows so that the tag with the most recent associated content is first.
		// If the tag happens to have no content, use 2000-01-01 as a default, so that it moves
		// to the bottom of the list.
		const sortedShows = copySort(restShows, (a, b) => {
			const aDate = new Date(a?.associatedContent?.nodes?.[0]?.date ?? '2000-01-01');
			const bDate = new Date(b?.associatedContent?.nodes?.[0]?.date ?? '2000-01-01');

			return bDate.getTime() - aDate.getTime();
		});

		// Join the two lists of shows together, so that pinned shows are still first.
		const joinedShows = pinnedShows.concat(sortedShows);

		// Shorten the descriptions so that they fit in 100 characters or less,
		// without breaking in the middle of words.
		/** @type {Array<import('../../components/FeaturedShows/FeaturedShowsTypes').Show>} */
		const showsWithTruncatedDescriptions = joinedShows.map((show) => {
			const description = show?.description;

			return ({
				...show,
				description,
			});
		});

		// Remove shows tagged "US Content" if traffic is from the US.
		const filteredShows = isUsTraffic(context)
			? removeShowsWithDistribution(showsWithTruncatedDescriptions, US_CONTENT)
			: showsWithTruncatedDescriptions;

		// If there are no featured shows available for US visitors,
		// redirect them to the homepage.
		if (filteredShows.length === 0) {
			return {
				redirect: {
					permanent: false,
					destination: REDIRECT_URL,
				},
				props: {},
			};
		}

		return {
			props: {
				id,
				shows: filteredShows,
				seo,
			},
		};
	} catch (_err) {
		// Return default props in case of error, so the page doesn't completely break with a 500 error.
		return {
			props: {
				id: '',
				shows: [],
				seo: {},
			},
		};
	}
}

FeaturedShows.propTypes = {
	id: PropTypes.string,
	shows: PropTypes.arrayOf(PropTypes.shape(FeaturedShowPropTypes)),
	seo: PropTypes.object,
};
